import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { AnimatedTextField, Button, Column, FullRow, Row, Typography } from 'seagull';
import { ArrowUp } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { usePromptHistory } from './hooks/usePromptHistory';
import styles from './PromptInputField.module.scss';
export const GetPromptInputFieldTID = (parentTestId)=>getTestIds(parentTestId);
export const PromptInputField = (param)=>{
    let { value, onChange, onSubmit, placeholder, suggestions = [], disabled = false, submitDisabled, leftActions, topContent, disclaimer, inputContainerRef, testId } = param;
    const { t } = useLocaleKeys();
    const { PromptInputFieldTID } = useMemo(()=>GetPromptInputFieldTID(testId), [
        testId
    ]);
    const computedSubmitDisabled = submitDisabled !== null && submitDisabled !== void 0 ? submitDisabled : disabled || value.trim().length === 0;
    const [topContentHeight, setTopContentHeight] = useState(0);
    const wrapperStyle = topContentHeight ? {
        '--top-content-height': "".concat(topContentHeight, "px")
    } : undefined;
    const resolvedDisclaimer = disclaimer === undefined ? t.common.aiSuggestionsDisclaimer() : disclaimer;
    const { recordSubmission, recallPrevious, recallNext } = usePromptHistory(value, onChange);
    const triggerSubmit = ()=>{
        if (computedSubmitDisabled) return;
        recordSubmission();
        onSubmit();
    };
    const handleKeyDown = (event)=>{
        if (event.nativeEvent.isComposing) return;
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            triggerSubmit();
            return;
        }
        const input = event.currentTarget;
        if (event.key === 'ArrowUp' && input.selectionStart === 0 && input.selectionEnd === 0) {
            if (recallPrevious(input)) event.preventDefault();
            return;
        }
        const inputLength = (value !== null && value !== void 0 ? value : '').length;
        const atInputEnd = input.selectionStart === inputLength && input.selectionEnd === inputLength;
        if (event.key === 'ArrowDown' && atInputEnd) {
            if (recallNext(input)) event.preventDefault();
        }
    };
    return /*#__PURE__*/ _jsxs(Column, {
        testId: PromptInputFieldTID.Root(),
        gap: 8,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.textFieldWrapper,
                style: wrapperStyle,
                children: [
                    topContent != null && /*#__PURE__*/ _jsx("div", {
                        className: styles.topContent,
                        ref: (el)=>setTopContentHeight(el ? el.offsetHeight : 0),
                        children: topContent
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.textFieldContainer,
                        ref: inputContainerRef,
                        children: /*#__PURE__*/ _jsx(AnimatedTextField, {
                            bi: {
                                name: 'ai_prompt'
                            },
                            suggestions: suggestions,
                            multiline: true,
                            minRows: 3,
                            maxRows: 5,
                            value: value,
                            onChange: (e)=>onChange(e.target.value),
                            inputProps: {
                                onKeyDown: handleKeyDown
                            },
                            disabled: disabled,
                            testId: PromptInputFieldTID.TextField(),
                            inputRootClass: styles.inputRoot,
                            placeholder: placeholder
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.footer,
                        children: /*#__PURE__*/ _jsxs(FullRow, {
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: 12,
                            children: [
                                /*#__PURE__*/ _jsx(Row, {
                                    gap: 8,
                                    children: leftActions
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'submit_ai_prompt'
                                    },
                                    onClick: triggerSubmit,
                                    disabled: computedSubmitDisabled,
                                    testId: PromptInputFieldTID.SubmitButton(),
                                    prefixIcon: /*#__PURE__*/ _jsx(ArrowUp, {
                                        size: 16
                                    }),
                                    className: styles.submitButton
                                })
                            ]
                        })
                    })
                ]
            }),
            resolvedDisclaimer != null && /*#__PURE__*/ _jsx(Typography, {
                className: styles.disclaimer,
                variant: "paragraph3",
                skin: "secondary",
                testId: PromptInputFieldTID.Disclaimer(),
                children: resolvedDisclaimer
            })
        ]
    });
};
